import { useContext, useState } from 'react';
import { ContentType } from 'types/publishedAssets';
import { ContentContainer } from 'components/Content/Atoms/ContentContainer';
import { useGetContentTranslations } from 'utils/useGetContentTranslations';
import { BlockHeading } from 'components/Content/Atoms/BlockHeading';
import { Icon } from 'components/Atoms/Icon/Icon';
import { ActionButton, ButtonType } from 'components/Atoms/ActionButton/ActionButton';
import { useTranslation } from 'react-i18next';
import { format, Locale } from 'date-fns';
import * as dateFnsLocales from 'date-fns/locale';
import { RenderMarkdownMarked } from 'components/Atoms/RenderMarkdownMarked/RenderMarkdownMarked';
import { determineDateObjectFromIso } from 'utils/determineDateValuesFromIso';
import { PageContext } from 'src/context/PageContext';
import { getInitialVisibleCount } from 'src/utils/getInitialVisibleCount';
import styles from './ImportantDate.module.scss';

interface Props {
  content: ContentType;
}

export function ImportantDate(props: Props) {
  const { urlParams } = useContext(PageContext);
  const { content } = props;
  const { translate } = useGetContentTranslations();
  const { t } = useTranslation();
  const showByDefault = getInitialVisibleCount(content.metadata, 0);
  const showPastDates = content.metadata?.show_past_dates || false;
  const displayOrder = content.metadata?.display_order || 'date-desc';
  const [showAllDates, setShowAllDates] = useState(false);
  const title = translate(content.translations, 'label');
  const description = translate(content.translations, 'description');

  const resolveDateFnsLocale = (localeCode: string | undefined): Locale | undefined => {
    if (!localeCode) return undefined;
    const toKey = (code: string) =>
      code
        .split('-')
        .map((part, idx) => (idx === 0 ? part.toLowerCase() : part.toUpperCase()))
        .join('');
    const exactKey = toKey(localeCode);
    if (exactKey in dateFnsLocales) return dateFnsLocales[exactKey as keyof typeof dateFnsLocales] as Locale;
    const baseKey = toKey(localeCode.split('-')[0]);
    if (baseKey in dateFnsLocales) return dateFnsLocales[baseKey as keyof typeof dateFnsLocales] as Locale;
    return undefined;
  };

  const dateLocale = resolveDateFnsLocale(urlParams?.locale);

  const formatDate = (dateValue: Date | string | undefined) => {
    if (!dateValue) return { day: '', monthYear: '' };

    try {
      const date = determineDateObjectFromIso(dateValue.toString());
      if (!date) return { day: '', monthYear: '' };
      const day = format(date, 'd', { locale: dateLocale });
      const monthYear = format(date, 'MMM yyyy', { locale: dateLocale });
      return { day, monthYear };
    } catch {
      return { day: '', monthYear: '' };
    }
  };

  let allDates = content.components.sort((lhs, rhs) => {
    const dateA = new Date(lhs.metadata?.date || '');
    const dateB = new Date(rhs.metadata?.date || '');
    if (!dateA || !dateB) return 0;
    return dateA.getTime() - dateB.getTime();
  });
  if (displayOrder === 'date-desc') {
    allDates = allDates.reverse();
  }
  if (!showPastDates) {
    const today = new Date();
    allDates = allDates.filter(
      (date) => date.metadata?.date && new Date(date.metadata?.date).getTime() > today.getTime(),
    );
  }
  const dateCount = allDates.length;

  if (dateCount === 0) {
    return null;
  }

  return (
    <ContentContainer content={content}>
      <div className={styles.dateContainer}>
        <BlockHeading title={title} secondaryText={description} id={content.id} />
        <div className={styles.datesList}>
          {allDates.map((component, index) => {
            if (showByDefault && !showAllDates && index > showByDefault - 1) return null;

            const dateValue = component?.metadata?.date;
            const { day, monthYear } = formatDate(dateValue);
            const dateTitle = translate(component.translations!, 'title');
            const dateDescription = translate(component.translations!, 'description');

            return (
              <div key={component.id} className={styles.dateEntry}>
                <div className={styles.dateDisplay}>
                  {day && <div className={styles.dateDay}>{day}</div>}
                  {monthYear && <div className={styles.dateMonthYear}>{monthYear}</div>}
                </div>
                <div className={styles.dateContent}>
                  {dateTitle && <h3 className={styles.dateTitle}>{dateTitle}</h3>}
                  {dateDescription && (
                    <RenderMarkdownMarked text={dateDescription} className={styles.dateDescription} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {showByDefault > 0 && dateCount > showByDefault && (
          <ActionButton
            className={styles.showAllDatesButton}
            type={ButtonType.ACTION_SECONDARY}
            onClick={() => setShowAllDates(!showAllDates)}
          >
            {showAllDates ? t('importantDate.showLess') : t('importantDate.showAllDates')}
            <Icon name={showAllDates ? 'chevron-up' : 'chevron-down'} />
          </ActionButton>
        )}
      </div>
    </ContentContainer>
  );
}
